import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PlanSelection, { Plan } from '../components/PlanSelection';
import PaymentForm from '../components/PaymentForm';

type RegistrationStep = 'account' | 'plan' | 'payment' | 'complete';

const plans: Plan[] = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    period: 'month',
    features: [
      '1,000 URLs per month',
      'Basic analytics',
      'Eddystone optimization',
      'API access'
    ]
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 299,
    period: 'month',
    features: [
      '50,000 URLs per month',
      'Advanced analytics',
      'Custom domains',
      'Bulk operations',
      'Priority support'
    ],
    popular: true
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 0, // Will display as "Custom"
    period: 'custom',
    features: [
      'Unlimited URLs',
      'White-label solution',
      'Dedicated support',
      'SLA guarantee',
      'Custom integrations'
    ]
  }
];

const Register: React.FC = () => {
  const [step, setStep] = useState<RegistrationStep>('account');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState('free');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const selectedPlan = plans.find(plan => plan.id === selectedPlanId) || plans[0];

  const handleAccountSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      return setError('Passwords do not match');
    }

    try {
      setError('');
      setLoading(true);
      await register(email, password);
      setStep('plan');
    } catch (error) {
      setError('Failed to create an account');
    } finally {
      setLoading(false);
    }
  };

  const handlePlanSelection = () => {
    if (selectedPlan.price === 0 && selectedPlan.period !== 'custom') {
      // Free plan - complete registration
      setStep('complete');
      setTimeout(() => navigate('/dashboard'), 2000);
    } else if (selectedPlan.period === 'custom') {
      // Enterprise plan - redirect to contact sales
      window.location.href = 'mailto:sales@rfdigi.co.za';
    } else {
      // Paid plan - proceed to payment
      setStep('payment');
    }
  };

  const handlePaymentSuccess = (subscriptionId: string) => {
    setStep('complete');
    setTimeout(() => navigate('/dashboard'), 2000);
  };

  const handlePaymentError = (error: string) => {
    setError(error);
  };

  const handleBackToPlans = () => {
    setError('');
    setStep('plan');
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      <div className="flex items-center space-x-4">
        <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-all duration-300 ${
          step === 'account' ? 'bg-gradient-to-r from-primary-500 to-orange-500 text-white shadow-macos' : 
          ['plan', 'payment', 'complete'].includes(step) ? 'bg-green-500 text-white shadow-macos' : 'bg-gray-700 text-gray-400 border border-gray-600'
        }`}>
          1
        </div>
        <div className={`w-12 h-0.5 transition-all duration-300 ${
          ['plan', 'payment', 'complete'].includes(step) ? 'bg-green-500' : 'bg-gray-600'
        }`} />
        <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-all duration-300 ${
          step === 'plan' ? 'bg-gradient-to-r from-primary-500 to-orange-500 text-white shadow-macos' : 
          ['payment', 'complete'].includes(step) ? 'bg-green-500 text-white shadow-macos' : 'bg-gray-700 text-gray-400 border border-gray-600'
        }`}>
          2
        </div>
        <div className={`w-12 h-0.5 transition-all duration-300 ${
          ['payment', 'complete'].includes(step) ? 'bg-green-500' : 'bg-gray-600'
        }`} />
        <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-all duration-300 ${
          step === 'payment' ? 'bg-gradient-to-r from-primary-500 to-orange-500 text-white shadow-macos' : 
          step === 'complete' ? 'bg-green-500 text-white shadow-macos' : 'bg-gray-700 text-gray-400 border border-gray-600'
        }`}>
          3
        </div>
      </div>
    </div>
  );

  const renderAccountForm = () => (
    <form className="space-y-6" onSubmit={handleAccountSubmit}>
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-macos text-sm backdrop-blur-sm">
          {error}
        </div>
      )}

      <div>
        <label htmlFor="email-address" className="block text-sm font-medium text-gray-300 mb-2">
          Email address
        </label>
        <input
          id="email-address"
          name="email"
          type="email"
          autoComplete="email"
          required
          className="block w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-macos placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 backdrop-blur-sm"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>

      <div>
        <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
          Password
        </label>
        <div className="relative">
          <input
            id="password"
            name="password"
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            required
            className="block w-full px-4 py-3 pr-12 bg-gray-700/50 border border-gray-600 rounded-macos placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 backdrop-blur-sm"
            placeholder="Create a password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button
            type="button"
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-300 transition-colors"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? (
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
              </svg>
            ) : (
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            )}
          </button>
        </div>
      </div>

      <div>
        <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-300 mb-2">
          Confirm Password
        </label>
        <div className="relative">
          <input
            id="confirm-password"
            name="confirm-password"
            type={showConfirmPassword ? "text" : "password"}
            autoComplete="new-password"
            required
            className="block w-full px-4 py-3 pr-12 bg-gray-700/50 border border-gray-600 rounded-macos placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 backdrop-blur-sm"
            placeholder="Confirm your password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          <button
            type="button"
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-300 transition-colors"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
          >
            {showConfirmPassword ? (
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
              </svg>
            ) : (
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            )}
          </button>
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full flex justify-center py-3 px-4 text-sm font-medium rounded-macos text-white bg-gradient-to-r from-primary-500 to-orange-500 hover:from-primary-600 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-macos hover:shadow-macos-lg transform hover:scale-105"
      >
        {loading ? (
          <div className="flex items-center">
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Creating account...
          </div>
        ) : (
          'Create account'
        )}
      </button>

      <div className="text-center">
        <span className="text-sm text-gray-400">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-primary-400 hover:text-primary-300 transition-colors">
            Sign in
          </Link>
        </span>
      </div>
    </form>
  );

  const renderPlanSelection = () => (
    <div className="space-y-6">
      <PlanSelection
        selectedPlan={selectedPlanId}
        onPlanSelect={setSelectedPlanId}
        loading={loading}
      />
      
      <div className="flex space-x-4">
        <button
          type="button"
          onClick={() => setStep('account')}
          className="flex-1 py-3 px-4 border border-gray-600 text-sm font-medium rounded-macos text-gray-300 bg-gray-700/50 hover:bg-gray-600/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-300 backdrop-blur-sm"
        >
          Back
        </button>
        <button
          type="button"
          onClick={handlePlanSelection}
          disabled={loading}
          className="flex-1 py-3 px-4 text-sm font-medium rounded-macos text-white bg-gradient-to-r from-primary-500 to-orange-500 hover:from-primary-600 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-macos hover:shadow-macos-lg transform hover:scale-105"
        >
          {selectedPlan.price === 0 && selectedPlan.period !== 'custom' 
            ? 'Complete Registration' 
            : selectedPlan.period === 'custom' 
            ? 'Contact Sales' 
            : 'Continue to Payment'}
        </button>
      </div>
    </div>
  );

  const renderPaymentForm = () => (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-macos text-sm backdrop-blur-sm">
          {error}
        </div>
      )}
      
      <PaymentForm
        selectedPlan={selectedPlan}
        onPaymentSuccess={handlePaymentSuccess}
        onPaymentError={handlePaymentError}
        loading={loading}
        setLoading={setLoading}
      />
      
      <button
        type="button"
        onClick={handleBackToPlans}
        className="w-full py-3 px-4 border border-gray-600 text-sm font-medium rounded-macos text-gray-300 bg-gray-700/50 hover:bg-gray-600/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-300 backdrop-blur-sm"
      >
        Back to Plans
      </button>
    </div>
  );

  const renderComplete = () => (
    <div className="text-center py-8">
      <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500/20 border border-green-500/30 rounded-full mb-4 backdrop-blur-sm">
        <svg className="w-8 h-8 text-green-400" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      </div>
      <h3 className="text-lg font-medium text-white mb-2">Welcome to RFUrl!</h3>
      <p className="text-sm text-gray-400 mb-4">
        Your account has been created successfully. You're being redirected to your dashboard.
      </p>
      <div className="inline-flex items-center text-sm text-primary-400">
        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Redirecting...
      </div>
    </div>
  );

  const getStepTitle = () => {
    switch (step) {
      case 'account':
        return 'Create your account';
      case 'plan':
        return 'Choose your plan';
      case 'payment':
        return 'Payment details';
      case 'complete':
        return 'Registration complete';
      default:
        return 'Create your account';
    }
  };

  const getStepDescription = () => {
    switch (step) {
      case 'account':
        return 'Get started with RFUrl - Professional URL shortening';
      case 'plan':
        return 'Select a plan that fits your needs';
      case 'payment':
        return 'Complete your subscription';
      case 'complete':
        return 'Welcome to RFUrl!';
      default:
        return 'Get started with RFUrl - Professional URL shortening';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23EF5000%22%20fill-opacity%3D%220.05%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20"></div>
      
      {/* Gradient Orbs */}
      <div className="absolute top-0 -left-4 w-72 h-72 bg-primary-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
      <div className="absolute top-0 -right-4 w-72 h-72 bg-orange-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-2000"></div>
      <div className="absolute -bottom-8 left-20 w-72 h-72 bg-red-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-4000"></div>
      
      <div className="relative max-w-4xl w-full">
        <div className="bg-gray-800/50 backdrop-blur-macos border border-gray-700 rounded-macos-xl shadow-macos-lg p-8">
          {renderStepIndicator()}
          
          <div className="text-center mb-8">
            <Link to="/" className="inline-block mb-6">
              <span className="text-2xl font-semibold bg-gradient-to-r from-primary-400 to-orange-400 bg-clip-text text-transparent">
                RFUrl
              </span>
            </Link>
            <h2 className="text-2xl font-semibold text-white">
              {getStepTitle()}
            </h2>
            <p className="mt-2 text-sm text-gray-400">
              {getStepDescription()}
            </p>
          </div>

          {step === 'account' && renderAccountForm()}
          {step === 'plan' && renderPlanSelection()}
          {step === 'payment' && renderPaymentForm()}
          {step === 'complete' && renderComplete()}
        </div>
        
        {/* Additional Links */}
        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-gray-400 hover:text-gray-300 transition-colors">
            ← Back to home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Register;